import { Modal, SubmitButton } from "@xola/ui-kit";
import React from "react";
import { ExperiencePurchaseItem, FieldType, FieldWithPickupOptions, Form, ProductObject } from "types";
import { NoTranslate } from "../Common/NoTranslate";
import { SecondaryButton } from "../Common/SecondaryButton";
import { getProductForm, isPickupLocationField } from "@/models/Form/Form";

export interface UnavailablePickupLocation {
    id: string;
    name: string;
}

export const getSelectedPickupLocations = (item: ExperiencePurchaseItem): UnavailablePickupLocation[] => {
    if (!item.formResponse?.fields) return [];

    return item.formResponse.fields
        .filter((field) => field.type === FieldType.PICKUP && field.pickupLocation?.id)
        .map((field) => ({
            id: field.pickupLocation!.id,
            name: field.pickupLocation!.name ?? field.value ?? "",
        }));
};

export const getUnavailablePickupLocations = (
    forms: Form[],
    experienceId: string,
    selectedPickupLocations: UnavailablePickupLocation[],
): UnavailablePickupLocation[] => {
    if (selectedPickupLocations.length === 0) return [];

    const resolvedForm =
        getProductForm(forms, experienceId) ??
        forms.find((f) => !f.product && f.productType === ProductObject.EXPERIENCE);

    if (!resolvedForm) {
        // New experience doesn't have a form, so the current pickup locations will be unavailable.
        return selectedPickupLocations;
    }

    const supportedPickupIds = new Set<string>();
    for (const field of resolvedForm.fields) {
        if (isPickupLocationField(field)) {
            for (const option of (field as FieldWithPickupOptions).options) {
                if (option.pickupLocation?.id) {
                    supportedPickupIds.add(option.pickupLocation.id);
                }
            }
        }
    }

    return selectedPickupLocations.filter((loc) => !supportedPickupIds.has(loc.id));
};

export interface PickupLocationsUnavailableModalProps {
    isOpen: boolean;
    sourceExperienceName: string;
    targetExperienceName: string;
    unavailableLocations: UnavailablePickupLocation[];
    onBack: () => void;
    onProceed: () => void;
}

export const PickupLocationsUnavailableModal = ({
    isOpen,
    sourceExperienceName,
    targetExperienceName,
    unavailableLocations,
    onBack,
    onProceed,
}: PickupLocationsUnavailableModalProps) => {
    return (
        <Modal isOpen={isOpen} onClose={onBack}>
            <PickupLocationsUnavailableModalBody
                sourceExperienceName={sourceExperienceName}
                targetExperienceName={targetExperienceName}
                unavailableLocations={unavailableLocations}
                onBack={onBack}
                onProceed={onProceed}
            />
        </Modal>
    );
};

interface PickupLocationsUnavailableModalBodyProps {
    sourceExperienceName: string;
    targetExperienceName: string;
    unavailableLocations: UnavailablePickupLocation[];
    onBack: () => void;
    onProceed: () => void;
}

const PickupLocationsUnavailableModalBody = ({
    sourceExperienceName,
    targetExperienceName,
    unavailableLocations,
    onBack,
    onProceed,
}: PickupLocationsUnavailableModalBodyProps) => {
    return (
        <>
            <Modal.Header>Pickup Locations Unavailable</Modal.Header>
            <Modal.Body>
                <div className="space-y-4">
                    <p className="p1">
                        The following pickup locations from{" "}
                        <strong>
                            <NoTranslate>{sourceExperienceName}</NoTranslate>
                        </strong>{" "}
                        are unavailable for{" "}
                        <strong>
                            <NoTranslate>{targetExperienceName}</NoTranslate>
                        </strong>
                        :
                    </p>

                    <ul className="list-disc pl-6">
                        {unavailableLocations.map((location) => (
                            <li key={location.id}>
                                <NoTranslate>{location.name}</NoTranslate>
                            </li>
                        ))}
                    </ul>

                    <p className="p1">
                        If you switch experiences, these locations will be retained despite being unavailable in{" "}
                        <strong>
                            <NoTranslate>{targetExperienceName}</NoTranslate>
                        </strong>
                        .
                    </p>
                </div>
            </Modal.Body>

            <Modal.Footer>
                <SecondaryButton onClick={onBack}>Back</SecondaryButton>
                <SubmitButton color="danger" onClick={onProceed}>
                    Proceed With Unavailable Pickup Locations
                </SubmitButton>
            </Modal.Footer>
        </>
    );
};
